import { useCallback, useMemo, useRef } from "react";
import { debounce } from "throttle-debounce";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useStorageSelectors } from "./use-storage-selectors";
import { editStorageObject } from "../actions/actions";
import {
  StorageScope,
  TValueForService,
} from "../../../../services/api/instances/storageApi/types/service_types";
import { createNotification } from "../../notificationSlice/slice";

export const useUpdateStorageValue = <S extends StorageScope>(
  service: S,
  setValue: ActionCreatorWithPayload<TValueForService<S>>,
  targetId?: string,
) => {
  const dispatch = useAppDispatch();
  const { selectedId, selectedObject, objects } = useStorageSelectors(service);

  const selectedIdRef = useRef(selectedId);
  const selectedObjectRef = useRef(selectedObject);
  const targetIdRef = useRef(targetId);

  selectedIdRef.current = selectedId;
  selectedObjectRef.current = selectedObject;
  targetIdRef.current = targetId;

  const saveToStorage = useCallback(
    (value: TValueForService<S>, name?: string) => {
      const id = selectedIdRef.current;
      const object = selectedObjectRef.current;
      const target = objects.find((obj) => obj.ref === targetIdRef.current);
      if (!id || !object) return;
      dispatch(
        editStorageObject({
          id: target ? target.id : id,
          name: name
            ? name
            : target
              ? target.friendly_name
              : object.friendly_name,
          value,
          service,
        }),
      );
    },
    [service, targetId, dispatch],
  );

  const debouncedSaveToStorage = useMemo(
    () => debounce(1000, saveToStorage),
    [saveToStorage],
  );

  const saveValue = useCallback(
    (value: TValueForService<S>, withDebounce = false, name?: string) => {
      if (name?.trim() === "") {
        dispatch(
          createNotification("warning", "Имя объекта не может быть пустым"),
        );
        return;
      }
      dispatch(setValue(value));
      if (withDebounce) {
        debouncedSaveToStorage(value, name);
      } else {
        saveToStorage(value, name);
      }
    },
    [dispatch, setValue, debouncedSaveToStorage, saveToStorage],
  );
  // useEffect(() => {
  //   return () => {
  //     debouncedSaveToStorage.cancel();
  //   };
  // }, [debouncedSaveToStorage]);

  return {
    saveValue,
  };
};
